// 2026-09-28 RUN 6 FASE 3 BL-01 (SEED=2026-09-28-eea43113, research 2026-09-28
// W3C C43 "Using CSS scroll-padding to un-obscure content" — WCAG 2.2 2.4.11
// Focus Not Obscured (Minimum) + 2.4.12 (Enhanced) — y jangwook.net 2026-08-04
// "Zero Failures Tabbing Down, 16 Tabbing Up: Sticky Headers" (medido con
// Playwright 1.57 + Chromium contra WCAG 2.2 Rec. 2024-12-12):
//
//   "el margen de scroll correcto es el alto del elemento pegajoso + holgura
//    para el anillo de foco, medido por separado en desktop y móvil" — con
//    `scroll-padding-top: calc(header + 1rem)` llevó 16 fallos AA a 0.
//
// El ToC de `/blog/[slug]` es `lg:sticky lg:top-20` y vive ENCIMA del cuerpo
// (una sola columna), así que ocupa de 80px hasta su propio borde inferior. Los
// `h2/h3` del markdown reciben `scroll-margin-top: 5rem` de `.prose-custom`
// (globals.css, SIN `@layer` ⇒ gana a cualquier utilidad de Tailwind), que
// además gana por especificidad al `[id] { 6rem }` global (0-1-1 vs 0-1-0).
// Destino a 80px = borde superior del ToC ⇒ el título quedaba tapado.
//
// Este módulo es PURO (sin "use client", sin React) para que el layout de
// servidor, el componente cliente y los tests compartan el mismo SSOT del
// offset, sin arrastrar el grafo del cliente al server component.
//
// Constantes medidas sobre el markup de `TableOfContents.tsx` (sin cambiarlo):
//   borde 1+1 + label `pt-4 pb-2` (16+8) + línea 11px ≈ 15 + `px-4` (16)
//   + `pb-4` (16) = 73
//   por <li>: `py-1` (8) + línea `text-sm` ≈ 20 = 28, más `space-y-1` (4).
import { slugifyHeading, uniqueHeadingId } from "@/lib/simple-markdown";

export interface TocItem {
  id: string;
  text: string;
  level: number;
}

/** `lg:top-20` del ToC pegajoso, en px. */
export const TOC_STICKY_TOP_PX = 80;
/** Holgura para el anillo de foco (WCAG 2.2 SC 2.4.13). */
export const TOC_FOCUS_SLACK_PX = 16;
/** Techo: por encima el ancla empuja el destino fuera del viewport. */
export const TOC_SCROLL_MT_MAX_PX = 720;
const TOC_CHROME_PX = 73;
const TOC_ITEM_PX = 28;
const TOC_GAP_PX = 4;

/**
 * `scroll-margin-top` que deja el heading del artículo por debajo del ToC
 * pegajoso. Puro y testeable; n >= 0.
 */
export function tocScrollMarginPx(headingCount: number): number {
  const n = Math.max(headingCount, 0);
  const height =
    TOC_CHROME_PX + n * TOC_ITEM_PX + Math.max(n - 1, 0) * TOC_GAP_PX;
  return Math.min(
    TOC_STICKY_TOP_PX + height + TOC_FOCUS_SLACK_PX,
    TOC_SCROLL_MT_MAX_PX,
  );
}

/**
 * Headings `#{1,3}` del cuerpo markdown, con los MISMOS ids que emite
 * `markdownToHtml` (slugify + dedup en orden de documento) para que los
 * anchors del índice coincidan con el HTML renderizado.
 *
 * 2026-09-21 FASE 3 FIXER design-docs-legal (research 2026 single-h1
 * BOIA/Moz): `#` de markdown se renderiza como h2, así que el ToC lo incluye
 * como nivel 2 para mantener la paridad con el HTML.
 */
export function extractHeadings(body: string): TocItem[] {
  const headings: TocItem[] = [];
  const seen = new Map<string, number>();
  const lines = body.split("\n");
  let inCodeBlock = false;
  let index = 0;

  for (const line of lines) {
    if (line.trim().startsWith("```")) {
      inCodeBlock = !inCodeBlock;
      continue;
    }
    if (inCodeBlock) continue;

    const match = line.match(/^(#{1,3})\s+(.+)/);
    if (match) {
      const rawLevel = match[1]!.length;
      const level = rawLevel === 1 ? 2 : rawLevel;
      const text = match[2]!.replace(/[*_`~]/g, "").trim();
      headings.push({
        id: uniqueHeadingId(slugifyHeading(text), index++, seen),
        text,
        level,
      });
    }
  }

  return headings;
}
